import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import { API_BASE_URL } from '../utils/constants';

/**
 * Low-latency presence tracker.
 * - Sends page_open on mount / route change
 * - Heartbeat every 15s while tab is visible (instant online detection)
 * - visibilitychange -> tab_hidden / tab_visible instantly
 * - beforeunload/pagehide -> page_close via sendBeacon (instant close)
 * - Also tracks page_view on route changes
 */
const HEARTBEAT_INTERVAL_MS = 15000; // 15s for ~instant online/offline detection
const ACTIVITY_ENDPOINT = '/auth/activity';
const HEARTBEAT_ENDPOINT = '/auth/heartbeat';
const BEACON_ENDPOINT = '/auth/heartbeat-beacon';

export default function useActivityTracker() {
  const { isAuthenticated } = useSelector((s) => s.auth);
  const location = useLocation();
  const intervalRef = useRef(null);
  const lastPageRef = useRef(null);

  const getPage = () => window.location.pathname + window.location.search;

  const sendHeartbeat = async (extra = {}) => {
    if (!isAuthenticated) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    try {
      await api.post(HEARTBEAT_ENDPOINT, { page: getPage(), event: 'heartbeat', ...extra }, { suppressGlobalLoading: true });
    } catch {}
  };

  const sendActivity = async (event, pageOverride) => {
    if (!isAuthenticated) return;
    const page = pageOverride || getPage();
    try {
      await api.post(ACTIVITY_ENDPOINT, { event, page }, { suppressGlobalLoading: true });
    } catch {}
  };

  // Heartbeat loop
  useEffect(() => {
    if (!isAuthenticated) return;
    // initial open
    sendActivity('page_open');
    sendHeartbeat({ event: 'page_open' });

    const startInterval = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') sendHeartbeat();
      }, HEARTBEAT_INTERVAL_MS);
    };
    startInterval();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        sendActivity('tab_visible');
        sendHeartbeat({ event: 'tab_visible' });
        startInterval();
      } else {
        sendActivity('tab_hidden');
        // also update session as hidden immediately
        api.post(HEARTBEAT_ENDPOINT, { page: getPage(), event: 'tab_hidden' }, { suppressGlobalLoading: true }).catch(() => {});
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    };

    const onBeforeUnload = () => {
      const url = `${API_BASE_URL}${BEACON_ENDPOINT}`;
      const data = JSON.stringify({ event: 'page_close', page: getPage() });
      try {
        if (navigator.sendBeacon) {
          const blob = new Blob([data], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
        } else {
          // fallback: sync xhr (best effort)
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url, false);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(data);
        }
      } catch {}
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onBeforeUnload);
      if (intervalRef.current) clearInterval(intervalRef.current);
      // try to send close on unmount (SPA navigation not counted as close)
      // we only beacon on true unload, not on React unmount
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Route change -> page_view (throttled)
  useEffect(() => {
    if (!isAuthenticated) return;
    const page = location.pathname + location.search;
    if (lastPageRef.current === page) return;
    lastPageRef.current = page;
    // debounce a little to avoid double firing on mount
    const t = setTimeout(() => {
      sendActivity('page_view', page);
      sendHeartbeat({ event: 'page_view', page });
    }, 300);
    return () => clearTimeout(t);
  }, [location.pathname, location.search, isAuthenticated]); // eslint-disable-line
}
